// ==========================
// Version 2 — components/AvatarUpload.js (circular avatar uploader — fixed template literals)
// - Uploads to Storage: avatar_uploads/<uid>/timestamp-name
// - Returns a downloadURL via onUploaded(url)
// - Shows progress + disabled state while uploading
// ==========================
import React, { useRef, useState } from 'react';
import { uploadUserAvatar } from '../db';
import BrandLogo from './BrandLogo';

const NAVY = '#0A1A33';
const GOLD = '#C9A44C';
const IVORY = '#FDFCF9';
// const WARM_GRAY = '#888888'; // not used here

export default function AvatarUpload({ uid, currentURL, onUploaded, size = 96 }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(0);

  const pick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Basic client-side guards (rules enforce too)
    if (!file.type.startsWith('image/')) {
      alert('Please select an image.');
      e.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('Image is larger than 5 MB. Please choose a smaller one.');
      e.target.value = '';
      return;
    }

    setBusy(true);
    setPct(0);
    try {
      const url = await uploadUserAvatar(uid, file, (progress) => setPct(progress));
      onUploaded?.(url);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[AvatarUpload] Failed:', err);
      alert('Upload failed. Please try again.');
    } finally {
      setBusy(false);
      e.target.value = '';
      setPct(0);
    }
  };

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
      <button
        type="button"
        onClick={pick}
        disabled={busy}
        style={{
          ...styles.wrap,
          width: size,
          height: size,
          cursor: busy ? 'progress' : 'pointer',
        }}
        aria-label="Change profile photo"
        title="Change profile photo"
      >
        {currentURL ? (
          <img src={currentURL} alt="" style={{ ...styles.img, width: size, height: size }} />
        ) : (
          <div style={{ ...styles.placeholder, width: size, height: size }}>
            <BrandLogo size={Math.max(38, Math.round(size * 0.58))} decorative />
          </div>
        )}

        {busy && (
          <div style={styles.busy}>
            <div style={styles.busyInner}>
              <div style={{ fontSize: 12, fontWeight: 800 }}>{pct}%</div>
              <div style={styles.progressBarOuter}>
                <div style={{ ...styles.progressBarInner, width: `${pct}%` }} />
              </div>
            </div>
          </div>
        )}
      </button>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={onFile}
        style={{ display: 'none' }}
      />

      <button
        type="button"
        onClick={pick}
        disabled={busy}
        style={styles.btn}
      >
        {busy ? 'Uploading…' : 'Change Photo'}
      </button>
    </div>
  );
}

const styles = {
  wrap: {
    position: 'relative',
    borderRadius: '50%',
    border: '1px solid rgba(201,164,76,0.35)',
    overflow: 'hidden',
    background: IVORY,
    boxShadow: '0 6px 18px rgba(10,26,51,0.25)',
    padding: 0,
  },
  img: {
    display: 'block',
    objectFit: 'cover',
    borderRadius: '50%',
  },
  placeholder: {
    display: 'grid',
    placeItems: 'center',
    borderRadius: '50%',
    color: NAVY,
    background: '#fff',
  },
  busy: {
    position: 'absolute',
    inset: 0,
    background: 'rgba(10,26,51,0.45)',
    display: 'grid',
    placeItems: 'center',
  },
  busyInner: {
    background: 'rgba(253,252,249,0.95)',
    color: NAVY,
    borderRadius: 10,
    padding: '6px 10px',
    border: '1px solid rgba(10,26,51,0.15)',
  },
  progressBarOuter: {
    width: 120,
    height: 6,
    borderRadius: 999,
    border: '1px solid rgba(10,26,51,0.15)',
    marginTop: 6,
    background: '#fff',
  },
  progressBarInner: {
    height: '100%',
    borderRadius: 999,
    background: GOLD,
  },
  btn: {
    background: 'transparent',
    color: GOLD,
    border: `1px solid ${GOLD}`,
    borderRadius: 12,
    padding: '8px 12px',
    fontWeight: 800,
    cursor: 'pointer',
  },
};
// ==========================
// End Version 2 — components/AvatarUpload.js
// ==========================
